import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import App from './App.tsx'
import { loadChannels } from './data/loadChannels.ts'
import { SESSION_ID } from './session.ts'
import { installChunkReload } from './chunkReload.ts'

// Injected by vite.config from Netlify's build env, so every Sentry event is tagged
// with the deploy (release = commit SHA) and context it came from.
declare const __SENTRY_RELEASE__: string
declare const __DEPLOY_CONTEXT__: string

// Errors + breadcrumbs only — deliberately NO browserTracing or Session Replay. Both
// run CONTINUOUSLY regardless of sample rate (replay buffers the DOM for on-error flush;
// browserTracing installs always-on Web-Vitals/INP/long-frame instrumentation that fired
// ~1100×/s in a profile), saturating the main thread AND the shared GPU compositor — it
// stuttered video in OTHER browser windows. captureMessage / addBreadcrumb / setTag (what
// the bug-report flow + `npm run sentry:session` use) need neither integration.
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  release: __SENTRY_RELEASE__,
  environment: __DEPLOY_CONTEXT__,
})
Sentry.setTag('session_id', SESSION_ID)

// A deploy rotates the hashed chunk names; an open tab's next lazy import() 404s and freezes whatever
// awaited it (e.g. a channel bumper). Reload to pick up the fresh chunk map. Installed before boot.
installChunkReload()

console.log(
  `%c ORION SESSION: ${SESSION_ID} `,
  'background:#cc0000;color:#fff;font-size:20px;font-weight:900;padding:6px 14px;border-radius:4px;letter-spacing:3px',
)

// Resolve the lineup BEFORE first render: the TV store's initial state derives
// from the channel list, and components hold no state — so the async load lives
// here at the boot boundary, not inside a component. Wrapped in a function to
// avoid top-level await (unsupported by the build's browser targets).
async function bootstrap() {
  const { channels } = await loadChannels()
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <Sentry.ErrorBoundary fallback={<p style={{ fontFamily: 'monospace', padding: '2rem' }}>Something went wrong — session: {SESSION_ID}</p>}>
        <App channels={channels} />
      </Sentry.ErrorBoundary>
    </StrictMode>,
  )
}

void bootstrap()
